<!-- Authenticated in-app route: /wisdom-care (see Org.vue "Wisdom Care" card, router.js). -->
<template>
    <div class="section text-center" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <template v-else>
        <div class="section mt-2" v-if="successMessage || errorMessage">
            <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>
            <div class="alert alert-danger" v-if="errorMessage">{{ errorMessage }}</div>
        </div>

        <!-- NOT YET REGISTERED -->
        <div class="section pt-0 mt-1 pb-2" v-if="!profile">
            <div class="listed-detail mt-1 mb-2">
                <p class="mal-text text-muted text-center">
                    'വിസ്ഡം കെയർ' വളണ്ടിയറായി രജിസ്റ്റർ ചെയ്യുക.
                </p>
            </div>
            <div class="card">
                <div class="card-body">
                    <Form @submit="submitRegister" :validation-schema="registerSchema">
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">ലിംഗം (Gender)</label>
                                <Field as="select" name="gender" class="form-control" v-model="registerFormData.gender">
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </Field>
                                <ErrorMessage name="gender" class="error-feedback" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">ജനന തീയതി (DOB)</label>
                                <Field type="date" name="dob" class="form-control" v-model="registerFormData.dob" />
                                <ErrorMessage name="dob" class="error-feedback" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">ഇമെയിൽ (Email)</label>
                                <Field type="email" name="email" class="form-control" v-model="registerFormData.email" />
                                <ErrorMessage name="email" class="error-feedback" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">വിലാസം (Address)</label>
                                <Field as="textarea" name="address" class="form-control" rows="2" v-model="registerFormData.address" />
                                <ErrorMessage name="address" class="error-feedback" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">താല്പര്യങ്ങളും കഴിവുകളും (Interests & Skills)</label>
                                <div class="form-check mb-1 col-12" v-for="c in serviceCategories" :key="c.id">
                                    <input type="checkbox" class="form-check-input" :id="'sc_' + c.id" :value="c.id" v-model="registerFormData.service_category_ids" />
                                    <label class="form-check-label mal-text" :for="'sc_' + c.id">{{ c.name }}<template v-if="c.name_mal"> ({{ c.name_mal }})</template></label>
                                </div>
                            </div>
                        </div>
                        <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="registering">
                            <span v-show="registering" class="spinner-border spinner-border-sm"></span>
                            <span class="mal-text">രജിസ്റ്റർ ചെയ്യുക (Register)</span>
                        </button>
                    </Form>
                </div>
            </div>
        </div>

        <!-- PROFILE VIEW -->
        <template v-else-if="!editingProfile">
            <div class="section mt-2">
                <div class="transactions">
                    <div class="item">
                        <div class="detail">
                            <div>
                                <strong class="mal-text">{{ profile.name }}</strong>
                                <p>{{ profile.mobile }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="item" v-if="profile.gender || profile.dob">
                        <div class="detail">
                            <div>
                                <strong class="mal-text">ലിംഗം / ജനന തീയതി</strong>
                                <p>{{ profile.gender || '-' }}<span v-if="profile.dob"> · {{ profile.dob }}</span></p>
                            </div>
                        </div>
                    </div>
                    <div class="item" v-if="profile.email">
                        <div class="detail">
                            <div>
                                <strong class="mal-text">ഇമെയിൽ</strong>
                                <p>{{ profile.email }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="item">
                        <div class="detail">
                            <div>
                                <strong class="mal-text">സ്ഥലം</strong>
                                <p>{{ profile.district_name }} / {{ profile.zone_name }} / {{ profile.unit_name }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="section mt-2">
                <div class="listed-detail">
                    <h3 class="mal-text">താല്പര്യങ്ങളും കഴിവുകളും</h3>
                </div>
                <div class="form-group basic" v-if="profile.service_categories.length">
                    <span class="badge bg-primary mal-text me-1 mb-1" v-for="c in profile.service_categories" :key="c.id">{{ c.name }}</span>
                </div>
                <p class="text-muted text-center" v-else>ഒന്നും തിരഞ്ഞെടുത്തിട്ടില്ല</p>
            </div>

            <div class="section mt-2 mb-2">
                <button type="button" class="btn btn-outline-primary btn-block mal-text" @click="startEditProfile">പ്രൊഫൈൽ എഡിറ്റ് ചെയ്യുക (Edit Profile)</button>
            </div>

            <!-- ORG TRAININGS -->
            <div class="section mt-3">
                <div class="listed-detail">
                    <h3 class="mal-text">സംഘടനാ ട്രെയിനിംഗുകൾ (Organizational Trainings)</h3>
                </div>
                <div class="transactions" v-if="profile.org_trainings.length">
                    <div class="item" v-for="t in profile.org_trainings" :key="'ot' + t.id">
                        <div class="detail">
                            <div>
                                <strong>{{ t.title }}</strong>
                                <p>{{ t.training_date }}<span v-if="t.location"> — {{ t.location }}</span></p>
                                <a v-if="t.certificate_file" :href="fileUrl(t.certificate_file)" target="_blank" class="mal-text">സർട്ടിഫിക്കറ്റ് കാണുക</a>
                            </div>
                        </div>
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="deleteOrgTraining(t.id)">
                            <ion-icon name="trash-outline"></ion-icon>
                        </button>
                    </div>
                </div>
                <p class="text-muted text-center" v-else>ഇതുവരെ ഒന്നും ചേർത്തിട്ടില്ല</p>

                <div class="mt-2" v-if="!showAddOrgTraining">
                    <button type="button" class="btn btn-outline-primary btn-block mal-text" @click="showAddOrgTraining = true">+ ട്രെയിനിംഗ് ചേർക്കുക</button>
                </div>
                <div class="card mt-2" v-else>
                    <div class="card-body">
                        <Form @submit="submitOrgTraining" :validation-schema="orgTrainingSchema">
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">ട്രെയിനിംഗ് പേര് <span class="text-danger">*</span></label>
                                    <Field type="text" name="title" class="form-control" v-model="newOrgTraining.title" />
                                    <ErrorMessage name="title" class="error-feedback" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">തീയതി <span class="text-danger">*</span></label>
                                    <Field type="date" name="training_date" class="form-control" v-model="newOrgTraining.training_date" />
                                    <ErrorMessage name="training_date" class="error-feedback" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">സ്ഥലം</label>
                                    <Field type="text" name="location" class="form-control" v-model="newOrgTraining.location" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">നടത്തിയത്</label>
                                    <Field type="text" name="conducted_by" class="form-control" v-model="newOrgTraining.conducted_by" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">സർട്ടിഫിക്കറ്റ്</label>
                                    <input type="file" class="form-control" accept=".pdf,.jpg,.jpeg,.png" @change="onOrgFileChange">
                                </div>
                            </div>
                            <button type="submit" class="btn btn-primary btn-block" :disabled="savingOrgTraining">
                                <span v-show="savingOrgTraining" class="spinner-border spinner-border-sm"></span>
                                <span class="mal-text">ചേർക്കുക (Add)</span>
                            </button>
                            <button type="button" class="btn btn-block btn-outline-secondary mt-2 mal-text" @click="showAddOrgTraining = false">ബാക്ക് (Back)</button>
                        </Form>
                    </div>
                </div>
            </div>

            <!-- EXTERNAL TRAININGS -->
            <div class="section mt-3 mb-3">
                <div class="listed-detail">
                    <h3 class="mal-text">പുറത്തുനിന്നുള്ള സർട്ടിഫിക്കറ്റുകൾ (External Certifications & Training)</h3>
                </div>
                <div class="transactions" v-if="profile.external_trainings.length">
                    <div class="item" v-for="t in profile.external_trainings" :key="'et' + t.id">
                        <div class="detail">
                            <div>
                                <strong>{{ t.title }}</strong>
                                <p>{{ t.issuing_agency }}<span v-if="t.training_date"> — {{ t.training_date }}</span></p>
                                <a v-if="t.certificate_file" :href="fileUrl(t.certificate_file)" target="_blank" class="mal-text">സർട്ടിഫിക്കറ്റ് കാണുക</a>
                            </div>
                        </div>
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="deleteExternalTraining(t.id)">
                            <ion-icon name="trash-outline"></ion-icon>
                        </button>
                    </div>
                </div>
                <p class="text-muted text-center" v-else>ഇതുവരെ ഒന്നും ചേർത്തിട്ടില്ല</p>

                <div class="mt-2" v-if="!showAddExternalTraining">
                    <button type="button" class="btn btn-outline-primary btn-block mal-text" @click="showAddExternalTraining = true">+ സർട്ടിഫിക്കറ്റ് ചേർക്കുക</button>
                </div>
                <div class="card mt-2" v-else>
                    <div class="card-body">
                        <Form @submit="submitExternalTraining" :validation-schema="externalTrainingSchema">
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">ട്രെയിനിംഗ് പേര് <span class="text-danger">*</span></label>
                                    <Field type="text" name="title" class="form-control" v-model="newExternalTraining.title" />
                                    <ErrorMessage name="title" class="error-feedback" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">നൽകിയ ഏജൻസി <span class="text-danger">*</span></label>
                                    <Field type="text" name="issuing_agency" class="form-control" v-model="newExternalTraining.issuing_agency" />
                                    <ErrorMessage name="issuing_agency" class="error-feedback" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">തീയതി</label>
                                    <Field type="date" name="training_date" class="form-control" v-model="newExternalTraining.training_date" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">സർട്ടിഫിക്കറ്റ് നമ്പർ</label>
                                    <Field type="text" name="certificate_number" class="form-control" v-model="newExternalTraining.certificate_number" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text text-primary">സർട്ടിഫിക്കറ്റ്</label>
                                    <input type="file" class="form-control" accept=".pdf,.jpg,.jpeg,.png" @change="onExternalFileChange">
                                </div>
                            </div>
                            <button type="submit" class="btn btn-primary btn-block" :disabled="savingExternalTraining">
                                <span v-show="savingExternalTraining" class="spinner-border spinner-border-sm"></span>
                                <span class="mal-text">ചേർക്കുക (Add)</span>
                            </button>
                            <button type="button" class="btn btn-block btn-outline-secondary mt-2 mal-text" @click="showAddExternalTraining = false">ബാക്ക് (Back)</button>
                        </Form>
                    </div>
                </div>
            </div>
        </template>

        <!-- PROFILE EDIT -->
        <div class="section pt-0 mt-1 pb-2" v-else>
            <div class="card">
                <div class="card-body">
                    <Form @submit="submitEditProfile" :validation-schema="registerSchema">
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">ലിംഗം (Gender)</label>
                                <Field as="select" name="gender" class="form-control" v-model="editFormData.gender">
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </Field>
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">ജനന തീയതി (DOB)</label>
                                <Field type="date" name="dob" class="form-control" v-model="editFormData.dob" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">ഇമെയിൽ (Email)</label>
                                <Field type="email" name="email" class="form-control" v-model="editFormData.email" />
                                <ErrorMessage name="email" class="error-feedback" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">വിലാസം (Address)</label>
                                <Field as="textarea" name="address" class="form-control" rows="2" v-model="editFormData.address" />
                            </div>
                        </div>
                        <div class="form-group basic">
                            <div class="input-wrapper">
                                <label class="label mal-text text-primary">താല്പര്യങ്ങളും കഴിവുകളും (Interests & Skills)</label>
                                <div class="form-check mb-1 col-12" v-for="c in serviceCategories" :key="c.id">
                                    <input type="checkbox" class="form-check-input" :id="'ec_sc_' + c.id" :value="c.id" v-model="editFormData.service_category_ids" />
                                    <label class="form-check-label mal-text" :for="'ec_sc_' + c.id">{{ c.name }}<template v-if="c.name_mal"> ({{ c.name_mal }})</template></label>
                                </div>
                            </div>
                        </div>
                        <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="savingProfile">
                            <span v-show="savingProfile" class="spinner-border spinner-border-sm"></span>
                            <span class="mal-text">സേവ് ചെയ്യുക (Save)</span>
                        </button>
                        <button type="button" class="btn btn-block btn-outline-secondary mt-2 mal-text" @click="editingProfile = false">ബാക്ക് (Back)</button>
                    </Form>
                </div>
            </div>
        </div>
    </template>
</template>

<script>
import { Form, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import UserService from "../services/user.service";

export default {
    name: "WisdomCare",
    components: { Form, Field, ErrorMessage },
    data() {
        return {
            loading: true,
            profile: null,
            serviceCategories: [],
            successMessage: "",
            errorMessage: "",

            registerSchema: yup.object({
                email: yup.string().email("സാധുവായ ഇമെയിൽ നൽകുക").nullable(),
            }),
            registerFormData: { gender: "", dob: "", email: "", address: "", service_category_ids: [] },
            registering: false,

            editingProfile: false,
            editFormData: { gender: "", dob: "", email: "", address: "", service_category_ids: [] },
            savingProfile: false,

            showAddOrgTraining: false,
            orgTrainingSchema: yup.object({
                title: yup.string().required("ഫിൽ ചെയ്യുക!"),
                training_date: yup.string().required("ഫിൽ ചെയ്യുക!"),
            }),
            newOrgTraining: { title: "", training_date: "", location: "", conducted_by: "", certificate_file: null },
            savingOrgTraining: false,

            showAddExternalTraining: false,
            externalTrainingSchema: yup.object({
                title: yup.string().required("ഫിൽ ചെയ്യുക!"),
                issuing_agency: yup.string().required("ഫിൽ ചെയ്യുക!"),
            }),
            newExternalTraining: { title: "", issuing_agency: "", training_date: "", certificate_number: "", certificate_file: null },
            savingExternalTraining: false,
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
    },
    mounted() {
        this.$root.updateParent("Wisdom Care");
        this.loadServiceCategories();
        this.loadProfile();
    },
    methods: {
        fileUrl(path) {
            return "https://guide.msoftit.com/storage/" + path;
        },
        flash(message, isError = false) {
            this.successMessage = isError ? "" : message;
            this.errorMessage = isError ? message : "";
            setTimeout(() => { this.successMessage = ""; this.errorMessage = ""; }, 4000);
        },
        loadServiceCategories() {
            UserService.authPostRequest("wisdom-care/categories", {}).then(
                (response) => { this.serviceCategories = response.data.data || []; },
                () => { this.serviceCategories = []; }
            );
        },
        loadProfile() {
            this.loading = true;
            UserService.authPostRequest("wisdom-care/my-profile", {}).then(
                (response) => {
                    this.profile = response.data.data || null;
                    this.loading = false;
                },
                () => {
                    this.profile = null;
                    this.loading = false;
                }
            );
        },
        submitRegister() {
            this.registering = true;
            UserService.authPostRequest("wisdom-care/register", this.registerFormData).then(
                (response) => {
                    this.registering = false;
                    if (response.data.status !== "success") {
                        this.flash(response.data.message || "രജിസ്ട്രേഷൻ പരാജയപ്പെട്ടു.", true);
                        return;
                    }
                    this.profile = response.data.data;
                    this.flash("രജിസ്ട്രേഷൻ വിജയകരമായി പൂർത്തിയായി.");
                },
                () => {
                    this.registering = false;
                    this.flash("എന്തോ തെറ്റ് സംഭവിച്ചു. ദയവായി വീണ്ടും ശ്രമിക്കുക.", true);
                }
            );
        },
        startEditProfile() {
            this.editFormData = {
                gender: this.profile.gender || "",
                dob: this.profile.dob || "",
                email: this.profile.email || "",
                address: this.profile.address || "",
                service_category_ids: this.profile.service_categories.map((c) => c.id),
            };
            this.editingProfile = true;
        },
        submitEditProfile() {
            this.savingProfile = true;
            UserService.authPostRequest("wisdom-care/update-profile", this.editFormData).then(
                (response) => {
                    this.savingProfile = false;
                    if (response.data.status !== "success") {
                        this.flash(response.data.message || "അപ്ഡേറ്റ് പരാജയപ്പെട്ടു.", true);
                        return;
                    }
                    this.profile = response.data.data;
                    this.editingProfile = false;
                    this.flash("പ്രൊഫൈൽ അപ്ഡേറ്റ് ചെയ്തു.");
                },
                () => {
                    this.savingProfile = false;
                    this.flash("എന്തോ തെറ്റ് സംഭവിച്ചു. ദയവായി വീണ്ടും ശ്രമിക്കുക.", true);
                }
            );
        },
        onOrgFileChange(e) {
            this.newOrgTraining.certificate_file = e.target.files[0] || null;
        },
        onExternalFileChange(e) {
            this.newExternalTraining.certificate_file = e.target.files[0] || null;
        },
        submitOrgTraining() {
            this.savingOrgTraining = true;
            const formData_ = new FormData();
            Object.keys(this.newOrgTraining).forEach((key) => {
                if (key === "certificate_file") {
                    if (this.newOrgTraining.certificate_file) formData_.append("certificate_file", this.newOrgTraining.certificate_file);
                } else if (this.newOrgTraining[key]) {
                    formData_.append(key, this.newOrgTraining[key]);
                }
            });
            UserService.authPostRequest("wisdom-care/org-trainings/add", formData_).then(
                (response) => {
                    this.savingOrgTraining = false;
                    if (response.data.status !== "success") {
                        this.flash(response.data.message || "ചേർക്കാൻ കഴിഞ്ഞില്ല.", true);
                        return;
                    }
                    this.newOrgTraining = { title: "", training_date: "", location: "", conducted_by: "", certificate_file: null };
                    this.showAddOrgTraining = false;
                    this.loadProfile();
                    this.flash("പരിശീലനം ചേർത്തു.");
                },
                () => {
                    this.savingOrgTraining = false;
                    this.flash("എന്തോ തെറ്റ് സംഭവിച്ചു. ദയവായി വീണ്ടും ശ്രമിക്കുക.", true);
                }
            );
        },
        deleteOrgTraining(trainingId) {
            UserService.authPostRequest("wisdom-care/org-trainings/delete", { training_id: trainingId }).then(
                (response) => {
                    if (response.data.status === "success") this.loadProfile();
                },
                () => { this.flash("നീക്കം ചെയ്യാൻ കഴിഞ്ഞില്ല.", true); }
            );
        },
        submitExternalTraining() {
            this.savingExternalTraining = true;
            const formData_ = new FormData();
            Object.keys(this.newExternalTraining).forEach((key) => {
                if (key === "certificate_file") {
                    if (this.newExternalTraining.certificate_file) formData_.append("certificate_file", this.newExternalTraining.certificate_file);
                } else if (this.newExternalTraining[key]) {
                    formData_.append(key, this.newExternalTraining[key]);
                }
            });
            UserService.authPostRequest("wisdom-care/external-trainings/add", formData_).then(
                (response) => {
                    this.savingExternalTraining = false;
                    if (response.data.status !== "success") {
                        this.flash(response.data.message || "ചേർക്കാൻ കഴിഞ്ഞില്ല.", true);
                        return;
                    }
                    this.newExternalTraining = { title: "", issuing_agency: "", training_date: "", certificate_number: "", certificate_file: null };
                    this.showAddExternalTraining = false;
                    this.loadProfile();
                    this.flash("സർട്ടിഫിക്കറ്റ് ചേർത്തു.");
                },
                () => {
                    this.savingExternalTraining = false;
                    this.flash("എന്തോ തെറ്റ് സംഭവിച്ചു. ദയവായി വീണ്ടും ശ്രമിക്കുക.", true);
                }
            );
        },
        deleteExternalTraining(trainingId) {
            UserService.authPostRequest("wisdom-care/external-trainings/delete", { training_id: trainingId }).then(
                (response) => {
                    if (response.data.status === "success") this.loadProfile();
                },
                () => { this.flash("നീക്കം ചെയ്യാൻ കഴിഞ്ഞില്ല.", true); }
            );
        },
    },
};
</script>
